import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Form, Formik, FormikHelpers } from "formik";
import isEqual from "lodash-es/isEqual";
import { useUserProfile } from "@providers/profile";
import { UserAddress, UserCard } from "@providers/profile/ProfileProvider.model";
import {
  creditCardAndAddressSchemaCA,
  creditCardAndAddressSchema,
  creditCardSchema,
} from "@utils/validations";
import { countryConfig } from "@utils/countryConfig";
import { formatErrorMessage } from "@utils/formatErrorMessage";
import { CardElementContainer, Input, Select, StripeError } from "@components/primitives/input";
import { addressInitialValues } from "../address/Address.model";
import { CreditCardFormProps, cardFormInitialValues, CardFormValues } from "./CreditCard.model";
import { CancelButton, ButtonWrap } from "../Account.styled";

// Format stripe card object to match returned card object from endpoint
const formatCardObject = (card) => {
  if (!card) throw new Error(`A card must be provided.`);

  const { brand, exp_month, exp_year, last4 } = card;

  return {
    brand,
    exp_month,
    exp_year,
    last4,
  };
};

// Remove name value from form values object to return
// an address object.
const formatAddressObject = (values: CardFormValues) => {
  const addressObject = values;
  delete addressObject.name;

  return addressObject as UserAddress;
};

const CreditCardForm = ({
  card,
  cardStatus,
  dispatchCardStatus,
  showAddress,
  dispatchBilling,
  states,
  onCancel,
}: CreditCardFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const creditCardAndLocalAddressSchema =
    process.env.NEXT_PUBLIC_COUNTRY_CODE === "CA"
      ? creditCardAndAddressSchemaCA
      : creditCardAndAddressSchema;
  const countryConfigData = countryConfig().address;
  const {
    attachPaymentMethod,
    changeAddress,
    profileState: { authToken },
  } = useUserProfile();

  const onSubmit = async (
    values: CardFormValues,
    { setSubmitting }: FormikHelpers<CardFormValues>
  ) => {
    let submitState = { ...cardStatus, error: "", stripeError: false };
    dispatchCardStatus({ ...submitState });
    dispatchBilling((prevState) => ({
      ...prevState,
      loading: true,
    }));
    setSubmitting(true);

    await updatePaymentMethod(submitState, values)
      .then((result) => {
        submitState = result;
      })
      .catch((error) => {
        submitState = error;
      });

    if (!submitState?.stripeError) {
      dispatchCardStatus({ ...submitState, editing: false });
    } else {
      // This should show the error message but keep the form open.
      dispatchCardStatus(submitState);
    }
    setSubmitting(false);
    dispatchBilling((prevState) => ({
      ...prevState,
      loading: false,
    }));
  };

  const updatePaymentMethod = async (submitState, cardValues) => {
    // Handle address update, since it's needed for the card
    let updatedAddress = null;

    if (showAddress) {
      updatedAddress = formatAddressObject(cardValues);
      const _changeResult = await changeAddress(authToken, updatedAddress);
    }

    // Fetch card data from stripe form input.
    const { paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
      billing_details: {
        name: cardValues.name,
        address: {
          ...(updatedAddress ? { ...updatedAddress } : {}),
        },
      },
    });

    // Get formatted card data
    let updatedCard;
    try {
      updatedCard = formatCardObject(paymentMethod.card);
    } catch (e) {
      submitState = { ...submitState, stripeError: true, error: "Missing values on the card form" };
      return submitState;
    }

    // Only proceed if submitted card is different than stored card.
    if (isEqual(updatedCard, card)) return;

    // Confirm card setup with fetched secret token
    const { setupIntent, error } = await stripe.confirmCardSetup(cardStatus.secret, {
      payment_method: {
        card: elements.getElement(CardElement),
        billing_details: {
          name: cardValues.name,
          address: {
            ...(updatedAddress ? { ...updatedAddress } : {}),
          },
        },
      },
    });

    // Stripe's api returns human-readable error messages
    if (error) {
      submitState = {
        ...submitState,
        error: error.message,
        stripeError: true,
      };
      return submitState;
    }

    // Only proceed if setup intent is valid.
    if (!setupIntent) {
      submitState = {
        ...submitState,
        error: "Invalid intent. Unable to submit the request at this time.",
        stripeError: true,
      };
      return submitState;
    }

    const _attachResult = await attachPaymentMethod(
      authToken,
      setupIntent.payment_method as string
    );

    submitState = { ...submitState, secret: null };
    dispatchBilling((prevState) => ({
      ...prevState,
      card: updatedCard,
      address: {
        ...prevState.address,
        ...(updatedAddress ? { ...updatedAddress } : {}),
      },
    }));

    return submitState;
  };

  return (
    <>
      <Formik
        initialValues={{
          ...cardFormInitialValues,
          ...(showAddress ? addressInitialValues : {}),
        }}
        validationSchema={showAddress ? creditCardAndLocalAddressSchema : creditCardSchema}
        onSubmit={onSubmit}>
        {(props) => {
          const { values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting } =
            props;
          return (
            <Form className="grid-x grid-margin-x" noValidate>
              <div className="cell small-12">
                <Input
                  type="text"
                  name="name"
                  label="Cardholder Name"
                  autoComplete="cc-name"
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  error={errors.name && touched.name && errors.name}
                />
              </div>
              <div className="cell small-12">
                {stripe ? (
                  <CardElementContainer>
                    <CardElement />
                  </CardElementContainer>
                ) : (
                  <StripeError />
                )}
              </div>

              {showAddress && (
                <>
                  <div className="cell small-12">
                    <h5>Billing Address</h5>
                  </div>
                  <div className="cell small-12">
                    <Input
                      type="text"
                      id="line1"
                      name="line1"
                      label="Address Line 1"
                      autoComplete="address-line1"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.line1}
                      error={errors.line1 && touched.line1 && errors.line1}
                    />
                  </div>
                  {countryConfigData.address2 && (
                    <div className="cell small-12">
                      <Input
                        type="text"
                        name="line2"
                        label="Apartment, Suite etc."
                        autoComplete="address-line2"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.line2}
                        error={errors.line2 && touched.line2 && errors.line2}
                      />
                    </div>
                  )}
                  <div className="cell small-12 medium-4">
                    <Input
                      type="text"
                      name="city"
                      label="City"
                      autoComplete="address-level2"
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.city}
                      error={errors.city && touched.city && errors.city}
                    />
                  </div>
                  <div className="cell small-12 medium-4">
                    <Select
                      name="state"
                      label={countryConfigData.stateLabel}
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.state}
                      error={errors.state && touched.state && errors.state}>
                      <option value={""} />
                      {states.map((s) => (
                        <option key={s._key} value={s.abbr}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="cell small-12 medium-4">
                    <Input
                      type="text"
                      name="postal_code"
                      label={countryConfigData.zipLabel}
                      autoComplete="postal-code"
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.postal_code}
                      error={errors.postal_code && touched.postal_code && errors.postal_code}
                    />
                  </div>
                </>
              )}

              <ButtonWrap className="cell small-12">
                <input
                  type="submit"
                  className="button inverted"
                  value={"Update"}
                  disabled={isSubmitting}
                />
                <CancelButton onClick={onCancel}>or cancel</CancelButton>
              </ButtonWrap>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default CreditCardForm;
